import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { 
	Modal, 
	IconButton, 
	Fade, 
	Backdrop, 
	Box, 
	Tooltip, 
	CircularProgress 
} from "@material-ui/core";
import { 
	Close as CloseIcon, 
	ZoomIn as ZoomInIcon, 
	ZoomOut as ZoomOutIcon 
} from "@material-ui/icons";

import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
	messageMedia: {
		objectFit: "cover",
		width: 250,
		height: 200,
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
		cursor: "pointer",
		transition: "opacity 0.2s",
		"&:hover": {
			opacity: 0.9
		}
	},
	modalContent: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		outline: "none",
		overflow: "hidden",
	},
	imageContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		height: "100%",
		overflow: "hidden", 
	},
	fullImage: {
		maxWidth: "90vw",
		maxHeight: "90vh",
		transformOrigin: "center center",
		cursor: "grab",
		"&:active": {
			cursor: "grabbing",
		},
	},
	controls: {
		position: "fixed",
		top: 20,
		right: 20,
		zIndex: 1301,
		backgroundColor: "rgba(0, 0, 0, 0.6)",
		borderRadius: 30,
		padding: "5px 10px",
		display: "flex",
		gap: 10,
	},
	iconButton: {
		color: "#fff",
		"&:hover": {
			backgroundColor: "rgba(255, 255, 255, 0.2)",
		}
	}
}));

const ModalImageCors = ({ imageUrl }) => {
	const classes = useStyles();
	const [fetching, setFetching] = useState(true);
	const [blobUrl, setBlobUrl] = useState("");
	const [open, setOpen] = useState(false);
	const [zoom, setZoom] = useState(1);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [startCoords, setStartCoords] = useState({ x: 0, y: 0 });
	const imageRef = useRef(null);

	useEffect(() => {
		if (!imageUrl) return;
		const fetchImage = async () => {
			try {
				const { data, headers } = await api.get(imageUrl, {
					responseType: "blob",
				});
				const url = window.URL.createObjectURL(
					new Blob([data], { type: headers["content-type"] })
				);
				setBlobUrl(url);
			} catch (error) {
				console.error("Error loading image", error);
			} finally {
				setFetching(false);
			}
		};
		fetchImage();
	}, [imageUrl]);

	const handleOpen = () => {
		setZoom(1);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setZoom(1);
		setPosition({ x: 0, y: 0 });
	};

	const handleZoomIn = (e) => {
		e && e.stopPropagation();
		setZoom((prev) => Math.min(prev + 0.5, 5));
	};

	const handleZoomOut = (e) => {
		e && e.stopPropagation();
		setZoom((prev) => Math.max(prev - 0.5, 0.5));
	};

	const handleWheel = (e) => {
		if (e.deltaY < 0) {
			setZoom((prev) => Math.min(prev + 0.2, 5));
		} else {
			setZoom((prev) => Math.max(prev - 0.2, 0.5));
		}
	};

	const handleMouseDown = (e) => {
		e.preventDefault();
		setIsDragging(true);
		setStartCoords({
			x: e.clientX - position.x,
			y: e.clientY - position.y
		});
	};

	const handleMouseMove = (e) => {
		if (!isDragging) return;
		e.preventDefault();
		setPosition({
			x: e.clientX - startCoords.x,
			y: e.clientY - startCoords.y
		});
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const altText = imageUrl ? imageUrl.split("/").pop() : "image";
	const displayUrl = fetching ? imageUrl : blobUrl;

	if (fetching) {
		return <div className={classes.messageMedia} style={{ display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#eee" }}>
			<CircularProgress size={20} />
		</div>;
	}

	return (
		<>
			<img
				src={displayUrl}
				alt={altText}
				className={classes.messageMedia}
				onClick={handleOpen}
			/>

			<Modal
				className={classes.modalContent}
				open={open}
				onClose={handleClose}
				closeAfterTransition
				BackdropComponent={Backdrop}
				BackdropProps={{
					timeout: 500,
					style: { backgroundColor: "rgba(0, 0, 0, 0.9)" }
				}}
			>
				<Fade in={open}>
					<div className={classes.modalContent}>
						<div className={classes.controls}>
							<Tooltip title="Alejar">
								<IconButton className={classes.iconButton} onClick={handleZoomOut} size="small">
									<ZoomOutIcon />
								</IconButton>
							</Tooltip>

							<Box color="white" fontWeight="bold" display="flex" alignItems="center" mx={1} minWidth={30} justifyContent="center">
								{Math.round(zoom * 100)}%
							</Box>

							<Tooltip title="Acercar">
								<IconButton className={classes.iconButton} onClick={handleZoomIn} size="small">
									<ZoomInIcon />
								</IconButton>
							</Tooltip>

							<Box height={24} width={1} bgcolor="rgba(255,255,255,0.3)" mx={1} />

							<Tooltip title="Cerrar">
								<IconButton className={classes.iconButton} onClick={handleClose} size="small">
									<CloseIcon />
								</IconButton>
							</Tooltip>
						</div>

						<div
							className={classes.imageContainer}
							onWheel={handleWheel}
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							onMouseLeave={handleMouseUp}
							onClick={(e) => {
								if (e.target === e.currentTarget && !isDragging) handleClose();
							}}
						>
							<img
								ref={imageRef}
								src={displayUrl}
								alt={altText}
								className={classes.fullImage}
								style={{
										transform: "translate(" + position.x + "px, " + position.y + "px) scale(" + zoom + ")",
										transition: isDragging ? "none" : "transform 0.1s ease-out"
								}}
								onClick={(e) => e.stopPropagation()}
								draggable={false}
							/>
						</div>
					</div>
				</Fade>
			</Modal>
		</>
	);
};

export default ModalImageCors;